import { LocalDate } from "@js-joda/core";
import type { MouseEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CompanyWorker } from "model/Company";
import { Api } from "services/api/api.service";
import { getWorkerImg } from "services/firebase/firebase.service";
import { useAlertStore } from "stores/alertStore/alertStore";

export type GenerateSalaryNoticesMode = "all" | "selected";
export type GenerateSalaryNoticesModalState =
  | "loading"
  | "error"
  | "empty"
  | "list";

interface UseGenerateSalaryNoticesModalParams {
  companyId: number | undefined;
  currentDate: LocalDate;
  onCreated: () => void;
}

interface UseGenerateSalaryNoticesModalReturn {
  avatarUrls: Record<string, string>;
  canCreateSalaryNotices: boolean;
  closeModal: () => void;
  createSalaryNotices: () => Promise<void>;
  generateSalaryNoticesMode: GenerateSalaryNoticesMode;
  handleGenerateSalaryNoticesModeChange: (value: string) => void;
  handleWorkerActionClick: (event: MouseEvent<HTMLDivElement>) => void;
  isGeneratingSalaryNotices: boolean;
  isOpen: boolean;
  modalState: GenerateSalaryNoticesModalState;
  openModal: () => void;
  salaryNoticeWorkers: CompanyWorker[];
  selectedWorkerIds: number[];
  toggleSelectedWorker: (workerId: number) => void;
}

type AvatarResult = {
  externalId: string;
  url: string;
};

const getWorkersWithExternalId = (
  workers: CompanyWorker[],
): (CompanyWorker & { externalId: string })[] =>
  workers.filter(
    (worker): worker is CompanyWorker & { externalId: string } =>
      worker.externalId !== null && worker.externalId !== "",
  );

const toAvatarUrls = (
  results: PromiseSettledResult<AvatarResult>[],
): Record<string, string> =>
  results.reduce<Record<string, string>>((acc, result) => {
    if (result.status !== "fulfilled") return acc;
    if (result.value.url) acc[result.value.externalId] = result.value.url;
    return acc;
  }, {});

const resolveModalState = (
  isLoadingWorkers: boolean,
  hasWorkerLoadFailed: boolean,
  workerCount: number,
): GenerateSalaryNoticesModalState => {
  if (isLoadingWorkers) return "loading";
  if (hasWorkerLoadFailed) return "error";
  if (workerCount === 0) return "empty";
  return "list";
};

export const useGenerateSalaryNoticesModal = (
  params: UseGenerateSalaryNoticesModalParams,
): UseGenerateSalaryNoticesModalReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [generateSalaryNoticesMode, setGenerateSalaryNoticesMode] =
    useState<GenerateSalaryNoticesMode>("all");
  const [isLoadingWorkers, setIsLoadingWorkers] = useState(false);
  const [hasWorkerLoadFailed, setHasWorkerLoadFailed] = useState(false);
  const [salaryNoticeWorkers, setSalaryNoticeWorkers] = useState<
    CompanyWorker[]
  >([]);
  const [avatarUrls, setAvatarUrls] = useState<Record<string, string>>({});
  const [selectedWorkerIds, setSelectedWorkerIds] = useState<number[]>([]);
  const [isGeneratingSalaryNotices, setIsGeneratingSalaryNotices] =
    useState(false);
  const isGeneratingSalaryNoticesRef = useRef(false);
  const { t } = useTranslation();
  const [, , alertDispatch] = useAlertStore();

  useEffect(() => {
    if (!isOpen) return;

    setGenerateSalaryNoticesMode("all");
    setSelectedWorkerIds([]);
    setAvatarUrls({});
    setHasWorkerLoadFailed(false);

    if (params.companyId === undefined) {
      setSalaryNoticeWorkers([]);
      setIsLoadingWorkers(false);
      return;
    }

    const companyId = params.companyId;
    let isMounted = true;

    const loadAvatars = async (workers: CompanyWorker[]): Promise<void> => {
      const results = await Promise.allSettled(
        getWorkersWithExternalId(workers).map(
          async (worker): Promise<AvatarResult> => {
            const url = await getWorkerImg(worker.externalId);
            return {
              externalId: worker.externalId,
              url: url ?? "",
            };
          },
        ),
      );
      if (!isMounted) return;
      setAvatarUrls(toAvatarUrls(results));
    };

    const loadWorkers = async (): Promise<void> => {
      setIsLoadingWorkers(true);
      try {
        const response = await Api().company.worker.getCompanyWorkersV2List(
          companyId,
          true,
        );
        if (!isMounted) return;
        setSalaryNoticeWorkers(response.data);
        setIsLoadingWorkers(false);
        void loadAvatars(response.data);
      } catch {
        if (!isMounted) return;
        setSalaryNoticeWorkers([]);
        setAvatarUrls({});
        setHasWorkerLoadFailed(true);
        setIsLoadingWorkers(false);
      }
    };

    void loadWorkers();

    return () => {
      isMounted = false;
    };
  }, [isOpen, params.companyId]);

  const toggleSelectedWorker = (workerId: number): void => {
    setSelectedWorkerIds((currentWorkerIds) =>
      currentWorkerIds.includes(workerId)
        ? currentWorkerIds.filter(
            (currentWorkerId) => currentWorkerId !== workerId,
          )
        : [...currentWorkerIds, workerId],
    );
  };

  const handleGenerateSalaryNoticesModeChange = (value: string): void => {
    if (value !== "all" && value !== "selected") return;
    setGenerateSalaryNoticesMode(value);
  };

  const handleWorkerActionClick = (event: MouseEvent<HTMLDivElement>): void => {
    event.stopPropagation();
  };

  const closeModal = (): void => {
    if (isGeneratingSalaryNotices) return;
    setIsOpen(false);
  };

  const openModal = (): void => {
    setIsOpen(true);
  };

  const canCreateSalaryNotices =
    params.companyId !== undefined &&
    !isLoadingWorkers &&
    !hasWorkerLoadFailed &&
    !isGeneratingSalaryNotices &&
    salaryNoticeWorkers.length > 0 &&
    (generateSalaryNoticesMode === "all" || selectedWorkerIds.length > 0);

  const modalState = resolveModalState(
    isLoadingWorkers,
    hasWorkerLoadFailed,
    salaryNoticeWorkers.length,
  );

  const createSalaryNotices = async (): Promise<void> => {
    if (isGeneratingSalaryNoticesRef.current) return;
    if (!canCreateSalaryNotices || params.companyId === undefined) return;

    const workerIds =
      generateSalaryNoticesMode === "selected" ? selectedWorkerIds : undefined;

    isGeneratingSalaryNoticesRef.current = true;
    setIsGeneratingSalaryNotices(true);
    try {
      await Api().company.salary.generateSalaryNotices(
        params.companyId,
        params.currentDate.year(),
        params.currentDate.monthValue(),
        workerIds,
      );
      setIsOpen(false);
      params.onCreated();
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "checkmark",
          title: t("Salary.GenerateSalaryNoticesCreatedTitle"),
          message: t("Salary.GenerateSalaryNoticesCreatedMessage"),
        },
      });
    } catch {
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "alert",
          title: t("Alerts.OhNo"),
          message: t("Alerts.SomethingWentWrong"),
        },
      });
    } finally {
      isGeneratingSalaryNoticesRef.current = false;
      setIsGeneratingSalaryNotices(false);
    }
  };

  return {
    avatarUrls,
    canCreateSalaryNotices,
    closeModal,
    createSalaryNotices,
    generateSalaryNoticesMode,
    handleGenerateSalaryNoticesModeChange,
    handleWorkerActionClick,
    isGeneratingSalaryNotices,
    isOpen,
    modalState,
    openModal,
    salaryNoticeWorkers,
    selectedWorkerIds,
    toggleSelectedWorker,
  };
};
